import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface FooterProps {
  onProgettoClick: () => void;
}

const Footer = ({ onProgettoClick }: FooterProps) => {
  const { t } = useTranslation();

  const menuLinks = [
    { label: t('nav.borghi'), href: '/borghi' },
    { label: t('nav.blog'), href: '/blog' },
    { label: t('nav.servizi'), href: '/servizi' },
    { label: 'Partner', href: '/partner' },
    { label: t('nav.contatti'), href: '/contatti' },
  ];

  return (
    <footer className="bg-anthracite-800 text-white">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-6 py-12 border-b border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
          {/* Brand */}
          <div>
            <img
              src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/d566f37bb7c274eb292da4befb3f457a.webp"
              alt="Marche Casa"
              className="h-10 w-auto object-contain mb-5"
            />
            <p className="font-sans text-sm text-white/50 leading-relaxed max-w-xs">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Links */}
          <div>
            <p className="font-sans text-xs tracking-widest uppercase text-white/30 mb-5">Menu</p>
            <div className="flex flex-col gap-3">
              {menuLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="font-sans text-sm text-white/60 hover:text-terracotta-200 transition-colors cursor-pointer"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/progetto"
                className="font-sans text-sm text-white/60 hover:text-terracotta-200 transition-colors cursor-pointer"
              >
                {t('footer.progetto')}
              </Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <p className="font-sans text-xs tracking-widest uppercase text-white/30 mb-5">{t('nav.contatti')}</p>
            <div className="flex flex-col gap-3">
              <p className="font-sans text-sm text-white/60">Castelfidardo (AN), Italia</p>
              <a
                href="https://www.instagram.com/felicity.dinallo_/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="font-sans text-sm text-white/60 hover:text-terracotta-200 transition-colors cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-instagram-line"></i>
                @felicity.dinallo_
              </a>
              <a
                href="https://www.instagram.com/marche_casa_your_dream_home/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="font-sans text-sm text-white/60 hover:text-terracotta-200 transition-colors cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-instagram-line"></i>
                @marche_casa_your_dream_home
              </a>
              <p className="font-sans text-sm text-white/60">🇮🇹 🇬🇧 🇪🇸</p>
            </div>
          </div>
        </div>
      </div>

      {/* FSE+ Compliance Block — MANDATORY, NON-COLLAPSIBLE */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10">
          {/* Institutional logos row */}
          <div className="flex justify-center mb-7">
            <img
              src="https://storage.readdy-site.link/project_files/eb454c64-f721-4c2b-844b-992754102f07/6eeb93c6-733f-4c44-b4e0-cff1d3cfa5af_Immagine-2026-04-04-114006.png?v=bd284cbda24a565510a290857dc4e7e5"
              alt="Coesione Italia 2021-2027 — Cofinanziato dall'Unione Europea — Repubblica Italiana — Regione Marche"
              className="w-full max-w-3xl h-auto object-contain"
            />
          </div>

          <p className="font-sans text-xs text-stone-500 text-center leading-relaxed max-w-3xl mx-auto mb-2">
            <strong className="text-stone-700">{t('footer.fse.title')}</strong>
          </p>
          <p className="font-sans text-xs text-stone-400 text-center leading-relaxed max-w-3xl mx-auto">
            {t('footer.fse.text')}
          </p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <p className="font-sans text-xs text-white/30">{t('footer.copyright')}</p>
            <span className="hidden sm:block text-white/20 text-xs">·</span>
            <p className="font-sans text-xs text-white/30">P.IVA 03036380420</p>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/progetto"
              className="font-sans text-xs text-white/60 hover:text-terracotta-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('footer.progetto')}
            </Link>
            <Link
              to="/privacy-policy"
              className="font-sans text-xs text-white/30 hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('footer.privacy')}
            </Link>
            <Link
              to="/cookie-policy"
              className="font-sans text-xs text-white/30 hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cookie Policy
            </Link>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openCookieConsent'))}
              className="font-sans text-xs text-white/30 hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap bg-transparent border-0 p-0"
            >
              Gestisci Cookie
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
